import { useT } from "../../../shared/i18n/i18n";
import { AlertTriangle, Code2, Plus, RotateCcw, Trash2, Variable, Wand2, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Fragment, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import type { WorkObjectFieldDefinition } from "../../../entities/work-object-field/model/use-work-object-fields";
import { useWorkObjects } from "../../../entities/work-object/model/use-work-objects";
import { FormulaExpressionEditor } from "../../work-object-fields/manage-field/ui/formula-expression-editor";
import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTRPC } from "@/shared/api/trpc";

type VisualOperator = "empty" | "eq" | "filled" | "gt" | "gte" | "lt" | "lte" | "neq";
type VisualConditionValue = { kind: "expression"; text: string } | { kind: "literal"; value: boolean | number | string | null };
type VisualCondition = { id: string; operator: VisualOperator; reference: string; value: VisualConditionValue };
type VisualStatus = "ready" | "unsupported";

export type PredicateBuilderState = {
  expression: string;
  hasIncomplete: boolean;
  isEmpty: boolean;
  isReady: boolean;
  mode: "formula" | "visual";
};

// Подписи операторов — ключи словаря: конструктор живёт и в фильтре вида,
// и в виджетах отчётов, и переводится вместе с остальным интерфейсом.
const operatorLabelKeys: Record<VisualOperator, string> = {
  empty: "filterBuilder.opEmpty",
  eq: "filterBuilder.opEquals",
  filled: "filterBuilder.opFilled",
  gt: "filterBuilder.opGreater",
  gte: "filterBuilder.opAtLeast",
  lt: "filterBuilder.opLess",
  lte: "filterBuilder.opAtMost",
  neq: "filterBuilder.opNotEquals",
};

const joinOptions = [
  { labelKey: "filterBuilder.joinAll", value: "all" },
  { labelKey: "filterBuilder.joinAny", value: "any" },
] as const;

const checkboxOptions = [
  { labelKey: "filterBuilder.checked", value: "true" },
  { labelKey: "filterBuilder.unchecked", value: "false" },
] as const;

/**
 * Shared visual/formula predicate editor. Owns both representations internally and keeps
 * them in sync bidirectionally; the parent observes the current state via `onStateChange`
 * and reloads from `initialExpression` whenever `resetSignal` changes.
 */
export function VisualPredicateBuilder({
  addLabel,
  disabled,
  emptyHint,
  externalError,
  fields,
  initialExpression,
  onStateChange,
  resetSignal,
}: {
  addLabel?: string;
  disabled: boolean;
  emptyHint: string;
  externalError?: string | null;
  fields: WorkObjectFieldDefinition[];
  initialExpression: string;
  onStateChange: (state: PredicateBuilderState) => void;
  resetSignal: number | string;
}) {
  const t = useT();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const fieldOptions = useMemo(() => fields.map((field) => ({ label: field.name, value: field.key })), [fields]);
  const [mode, setMode] = useState<"formula" | "visual">("visual");
  const [join, setJoin] = useState<"all" | "any">("all");
  const [conditions, setConditions] = useState<VisualCondition[]>([]);
  const [expression, setExpression] = useState(initialExpression);
  const [visualStatus, setVisualStatus] = useState<VisualStatus>("ready");
  const [unsupportedReason, setUnsupportedReason] = useState<string | null>(null);

  // Guard against stale async responses overwriting fresher state.
  const formulaToVisualRef = useRef(0);
  const visualToFormulaRef = useRef(0);
  const initialExpressionRef = useRef(initialExpression);
  initialExpressionRef.current = initialExpression;
  const onStateChangeRef = useRef(onStateChange);
  onStateChangeRef.current = onStateChange;

  // Reload both representations whenever the parent bumps `resetSignal`.
  useEffect(() => {
    const requestId = ++formulaToVisualRef.current;
    const currentExpression = initialExpressionRef.current;
    setExpression(currentExpression);
    if (!currentExpression) {
      setVisualStatus("ready");
      setUnsupportedReason(null);
      setMode("visual");
      setJoin("all");
      setConditions([]);
      return;
    }
    void queryClient.fetchQuery(trpc.boards.inspectStatusRule.queryOptions({ expression: currentExpression }))
      .then((inspection) => {
        if (requestId !== formulaToVisualRef.current) return;
        const projected = inspection.visualProjection.ok ? visualConditionsFromProjection(inspection.visualProjection.value) : null;
        if (!inspection.visualProjection.ok) {
          markUnsupported(inspection.visualProjection.reason);
          return;
        }
        if (!projected) {
          markUnsupported(t("filterBuilder.nestedGroups"));
          return;
        }
        setVisualStatus("ready");
        setUnsupportedReason(null);
        setJoin(projected.join);
        setConditions(projected.conditions);
        setMode("visual");
      })
      .catch((reason: unknown) => {
        if (requestId !== formulaToVisualRef.current) return;
        markUnsupported(reason instanceof Error ? reason.message : t("filterBuilder.couldNotRead"));
      });

    function markUnsupported(reason: string) {
      setVisualStatus("unsupported");
      setUnsupportedReason(reason);
      setMode("formula");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignal]);

  // Live visual -> formula: keep the expression text in sync while building visually.
  useEffect(() => {
    if (mode !== "visual" || visualStatus !== "ready" || conditions.length === 0) return;
    const handle = setTimeout(() => {
      const requestId = ++visualToFormulaRef.current;
      void queryClient.fetchQuery(trpc.boards.generateStatusRule.queryOptions({
        predicate: { children: conditions.map(({ id: _id, ...condition }) => ({ kind: "condition" as const, ...condition })), kind: join },
      }))
        .then((result) => {
          if (requestId !== visualToFormulaRef.current) return;
          setExpression(result.expression);
        })
        .catch(() => {});
    }, 200);
    return () => clearTimeout(handle);
  }, [mode, visualStatus, join, conditions]);

  // Live formula -> visual: mirror the builder while typing a formula, or flag it as unsupported.
  useEffect(() => {
    if (mode !== "formula") return;
    const trimmed = expression.trim();
    if (!trimmed) {
      setVisualStatus("ready");
      setUnsupportedReason(null);
      setConditions([]);
      return;
    }
    const handle = setTimeout(() => {
      const requestId = ++formulaToVisualRef.current;
      void queryClient.fetchQuery(trpc.boards.inspectStatusRule.queryOptions({ expression: trimmed }))
        .then((inspection) => {
          if (requestId !== formulaToVisualRef.current) return;
          const projected = inspection.visualProjection.ok ? visualConditionsFromProjection(inspection.visualProjection.value) : null;
          if (!inspection.visualProjection.ok) {
            setVisualStatus("unsupported");
            setUnsupportedReason(inspection.visualProjection.reason);
            return;
          }
          if (!projected) {
            setVisualStatus("unsupported");
            setUnsupportedReason(t("filterBuilder.nestedGroups"));
            return;
          }
          setVisualStatus("ready");
          setUnsupportedReason(null);
          setJoin(projected.join);
          setConditions(projected.conditions);
        })
        .catch((reason: unknown) => {
          if (requestId !== formulaToVisualRef.current) return;
          setVisualStatus("unsupported");
          setUnsupportedReason(reason instanceof Error ? reason.message : t("filterBuilder.couldNotParse"));
        });
    }, 300);
    return () => clearTimeout(handle);
  }, [mode, expression]);

  // Условие без значения нельзя сохранить ни в каком виде: пустая формула
  // и невыбранный элемент списка одинаково дали бы `= null`, который
  // совпадает только с пустыми полями — не то, что человек имел в виду.
  const hasIncomplete = conditions.some((item) => {
    if (item.operator === "empty" || item.operator === "filled") return false;
    if (item.value.kind === "expression") return !item.value.text.trim();
    return item.value.value === null || item.value.value === "";
  });
  const isEmpty = mode === "visual" ? conditions.length === 0 : !expression.trim();
  const isReady = visualStatus === "ready";

  // Surface the live state to the parent (footer/save controls live there).
  useEffect(() => {
    onStateChangeRef.current({ expression, hasIncomplete, isEmpty, isReady, mode });
  }, [expression, hasIncomplete, isEmpty, isReady, mode]);

  function resetVisualBuilder() {
    formulaToVisualRef.current += 1;
    setVisualStatus("ready");
    setUnsupportedReason(null);
    setJoin("all");
    setConditions([createCondition(fields[0])]);
  }

  return (
    <Tabs className="@container" onValueChange={(value) => setMode(value as "formula" | "visual")} value={mode}>
      <TabsList>
        <TabsTrigger value="visual"><Wand2 data-icon="inline-start" /> {t("filterBuilder.builder")}</TabsTrigger>
        <TabsTrigger value="formula"><Code2 data-icon="inline-start" /> {t("filterBuilder.formula")}</TabsTrigger>
      </TabsList>

      <TabsContent className="mt-4" value="visual">
        {visualStatus === "unsupported" ? (
          <UnsupportedVisualPanel expression={expression} onEditFormula={() => setMode("formula")} onReset={resetVisualBuilder} reason={unsupportedReason} />
        ) : (
          <div className="flex flex-col gap-4">
            {conditions.length > 1 ? (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-muted-foreground">{t("filterBuilder.match")}</span>
                <Select items={joinOptions.map((option) => ({ label: t(option.labelKey), value: option.value }))} onValueChange={(value) => value && setJoin(value as "all" | "any")} value={join}>
                  <SelectTrigger className="h-8 w-auto gap-1.5 px-2.5 font-medium" aria-label={t("filterBuilder.matchMode")}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectGroup>{joinOptions.map((option) => <SelectItem key={option.value} value={option.value}>{t(option.labelKey)}</SelectItem>)}</SelectGroup></SelectContent>
                </Select>
                <span className="text-muted-foreground">{t("filterBuilder.ofConditionsBelow")}</span>
              </div>
            ) : null}

            {conditions.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border/70 px-3 py-4 text-center text-sm text-muted-foreground">{emptyHint}</p>
            ) : (
              <div className="flex flex-col">
                {conditions.map((condition, index) => (
                  <Fragment key={condition.id}>
                    {index > 0 ? <ConditionConnector label={join === "all" ? t("board.andOperator") : t("filterBuilder.orOperator")} /> : null}
                    <VisualConditionRow
                      condition={condition}
                      disabled={disabled}
                      fields={fields}
                      fieldOptions={fieldOptions}
                      onChange={(next) => setConditions((current) => current.map((item) => (item.id === next.id ? next : item)))}
                      onRemove={() => setConditions((current) => current.filter((item) => item.id !== condition.id))}
                    />
                  </Fragment>
                ))}
              </div>
            )}

            <Button className="w-fit" disabled={disabled || fields.length === 0} onClick={() => setConditions((current) => [...current, createCondition(fields[0])])} type="button" variant="outline">
              <Plus data-icon="inline-start" /> {addLabel ?? t("filterBuilder.addCondition")}
            </Button>
          </div>
        )}
      </TabsContent>

      <TabsContent className="mt-4" value="formula">
        <div className="flex flex-col gap-3">
          <FormulaExpressionEditor
            currentFieldId={null}
            disabled={disabled}
            externalError={externalError}
            fields={fields}
            onChange={setExpression}
            scopeFieldIds={fields.map((field) => field.id)}
            showValidationMessages
            value={expression}
          />
          <FormulaCompatibilityNote reason={unsupportedReason} status={visualStatus} />
        </div>
      </TabsContent>
    </Tabs>
  );
}

function UnsupportedVisualPanel({ expression, onEditFormula, onReset, reason }: { expression: string; onEditFormula: () => void; onReset: () => void; reason: string | null }) {
  const t = useT();
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium">{t("filterBuilder.notShown")}</p>
          <p className="text-sm text-muted-foreground">{reason ?? t("filterBuilder.unsupportedSyntax")}</p>
        </div>
      </div>
      {expression.trim() ? <pre className="overflow-x-auto rounded-md border border-border/60 bg-muted/40 px-3 py-2 font-mono text-xs text-foreground">{expression}</pre> : null}
      <div className="flex flex-wrap gap-2">
        <Button onClick={onEditFormula} size="sm" type="button" variant="outline"><Code2 data-icon="inline-start" /> {t("filterBuilder.editInFormula")}</Button>
        <Button onClick={onReset} size="sm" type="button" variant="ghost"><RotateCcw data-icon="inline-start" /> {t("filterBuilder.resetBuilder")}</Button>
      </div>
    </div>
  );
}

function FormulaCompatibilityNote({ reason, status }: { reason: string | null; status: VisualStatus }) {
  const t = useT();
  if (status === "ready") return null;
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2">
      <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
      <span className="text-xs text-muted-foreground">{reason ?? t("filterBuilder.advancedSyntaxOnly")}</span>
    </div>
  );
}

function ConditionConnector({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 py-1.5 pl-3">
      <span className="rounded border border-border/70 bg-muted/40 px-2 py-0.5 text-[0.625rem] font-semibold tracking-wide text-muted-foreground">{label}</span>
      <span className="h-px flex-1 bg-border/50" />
    </div>
  );
}

function VisualConditionRow({ condition, disabled, fields, fieldOptions, onChange, onRemove }: { condition: VisualCondition; disabled: boolean; fields: WorkObjectFieldDefinition[]; fieldOptions: Array<{ label: string; value: string }>; onChange: (condition: VisualCondition) => void; onRemove: () => void }) {
  const t = useT();
  const field = fields.find((candidate) => candidate.key === condition.reference) ?? fields[0];
  const needsValue = condition.operator !== "empty" && condition.operator !== "filled";
  const operatorOptions = operatorsForField(field).map((operator) => ({ label: t(operatorLabelKeys[operator]), value: operator }));
  const isExpression = needsValue && condition.value.kind === "expression";
  const showInlineValue = needsValue && condition.value.kind === "literal";
  const scopeFieldIds = useMemo(() => fields.map((candidate) => candidate.id), [fields]);
  const gridClass = showInlineValue
    ? "@md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1.2fr)_2.25rem]"
    : "@md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_2.25rem]";
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border/70 bg-muted/15 p-3">
      <div className={`grid gap-3 @md:items-end ${gridClass}`}>
        <Field>
          <FieldLabel>{t("filterBuilder.field")}</FieldLabel>
          <Select items={fieldOptions} onValueChange={(reference) => reference && onChange({ ...condition, reference })} value={condition.reference}>
            <SelectTrigger className="w-full" aria-label={t("filterBuilder.conditionField")}><SelectValue /></SelectTrigger>
            <SelectContent><SelectGroup>{fieldOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectGroup></SelectContent>
          </Select>
        </Field>
        <Field>
          <FieldLabel>{t("filterBuilder.condition")}</FieldLabel>
          <Select items={operatorOptions} onValueChange={(operator) => operator && onChange({ ...condition, operator: operator as VisualOperator })} value={condition.operator}>
            <SelectTrigger className="w-full" aria-label={t("filterBuilder.conditionOperator")}><SelectValue /></SelectTrigger>
            <SelectContent><SelectGroup>{operatorOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectGroup></SelectContent>
          </Select>
        </Field>
        {needsValue && condition.value.kind === "literal" ? (
          <Field>
            <FieldLabel>{t("filterBuilder.value")}</FieldLabel>
            <ConditionValue field={field} onChange={(value) => onChange({ ...condition, value })} value={condition.value} />
          </Field>
        ) : null}
        <Field className="items-end @md:justify-end [&>button]:w-auto">
          <FieldLabel className="sr-only">{t("filterBuilder.conditionActions")}</FieldLabel>
          <Button aria-label={t("filterBuilder.removeCondition")} onClick={onRemove} size="icon-sm" type="button" variant="ghost"><Trash2 /></Button>
        </Field>
      </div>
      {isExpression ? (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-2">
            <FieldLabel className="mb-0 flex items-center gap-1.5">
              {t("filterBuilder.value")}
              <span className="rounded bg-muted px-1 py-px font-mono text-[0.625rem] font-medium text-muted-foreground">ƒx</span>
            </FieldLabel>
            <Button className="-mr-1 h-6 px-1.5 text-xs text-muted-foreground hover:text-foreground" onClick={() => onChange({ ...condition, value: { kind: "literal", value: null } })} size="sm" type="button" variant="ghost"><X data-icon="inline-start" /> {t("filterBuilder.useFixedValue")}</Button>
          </div>
          <FormulaExpressionEditor
            compact
            currentFieldId={null}
            disabled={disabled}
            fields={fields}
            onChange={(text) => onChange({ ...condition, value: { kind: "expression", text } })}
            scopeFieldIds={scopeFieldIds}
            showValidationMessages
            value={condition.value.kind === "expression" ? condition.value.text : ""}
          />
        </div>
      ) : null}
    </div>
  );
}

const inputGroupClass = "flex h-7 w-full items-center rounded-md border border-input bg-input/20 transition-colors focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30 dark:bg-input/30";
const bareTriggerClass = "h-full flex-1 border-0 bg-transparent px-2 shadow-none focus-visible:ring-0";

function ConditionValue({ field, onChange, value }: { field: WorkObjectFieldDefinition | undefined; onChange: (value: VisualConditionValue) => void; value: { kind: "literal"; value: boolean | number | string | null } }) {
  const t = useT();
  // Типы и люди — те же списки, что у остальных экранов; в фильтре сохраняется
  // id, потому что именно с id сравнивает запрос.
  const workObjects = useWorkObjects();
  const setLiteral = (next: boolean | number | string | null) => onChange({ kind: "literal", value: next });
  const literal = value.value;
  const idOptions =
    field?.key === "type"
      ? (workObjects.data?.types ?? []).map((type) => ({ label: type.name, value: type.id }))
      : field?.type === "user_relation" || field?.type === "user_multi_relation"
        ? (workObjects.data?.users ?? []).map((user) => ({ label: user.displayName ?? user.email, value: user.id }))
        : null;
  const control =
    idOptions ? (
      <Select items={idOptions} onValueChange={(next) => setLiteral(next ?? "")} value={typeof literal === "string" ? literal : ""}><SelectTrigger aria-label={t("filterBuilder.conditionValue")} className={bareTriggerClass}><SelectValue placeholder={t("filterBuilder.selectValue")} /></SelectTrigger><SelectContent><SelectGroup>{idOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectGroup></SelectContent></Select>
    ) : field?.type === "select" ? (
      (() => {
        const options = field.options.map((option) => ({ label: option.value, value: option.id }));
        return <Select items={options} onValueChange={(next) => setLiteral(next ?? "")} value={typeof literal === "string" ? literal : ""}><SelectTrigger aria-label={t("filterBuilder.conditionValue")} className={bareTriggerClass}><SelectValue placeholder={t("filterBuilder.selectValue")} /></SelectTrigger><SelectContent><SelectGroup>{options.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectGroup></SelectContent></Select>;
      })()
    ) : field?.type === "checkbox" ? (
      <Select items={checkboxOptions.map((option) => ({ label: t(option.labelKey), value: option.value }))} onValueChange={(next) => setLiteral(next === "true")} value={String(Boolean(literal))}><SelectTrigger aria-label={t("filterBuilder.conditionValue")} className={bareTriggerClass}><SelectValue /></SelectTrigger><SelectContent><SelectGroup>{checkboxOptions.map((option) => <SelectItem key={option.value} value={option.value}>{t(option.labelKey)}</SelectItem>)}</SelectGroup></SelectContent></Select>
    ) : (
      <input aria-label={t("filterBuilder.conditionValue")} className="h-full min-w-0 flex-1 bg-transparent px-2 text-sm outline-none placeholder:text-muted-foreground" onChange={(event) => setLiteral(isNumericField(field) ? Number(event.target.value) : event.target.value)} placeholder={t("filterBuilder.enterValue")} type={isNumericField(field) ? "number" : "text"} value={typeof literal === "string" || typeof literal === "number" ? literal : ""} />
    );

  return (
    <div className={inputGroupClass}>
      {control}
      <AppendButton icon={<Variable />} label={t("filterBuilder.useFormula")} onClick={() => onChange({ kind: "expression", text: "" })} />
    </div>
  );
}

function AppendButton({ icon, label, onClick }: { icon: ReactNode; label: string; onClick: () => void }) {
  return (
    <Button aria-label={label} className="mr-0.5 size-6 shrink-0 text-muted-foreground hover:text-foreground" onClick={onClick} size="icon-sm" type="button" variant="ghost">
      {icon}
    </Button>
  );
}

function createCondition(field: WorkObjectFieldDefinition | undefined): VisualCondition {
  return { id: crypto.randomUUID(), operator: "filled", reference: field?.key ?? "title", value: { kind: "literal", value: null } };
}

function normalizeProjectedValue(value: unknown): VisualConditionValue {
  if (value && typeof value === "object" && "kind" in value) {
    const parsed = value as { kind: string; text?: string; value?: boolean | number | string | null };
    if (parsed.kind === "expression" && typeof parsed.text === "string") return { kind: "expression", text: parsed.text };
    if (parsed.kind === "literal") return { kind: "literal", value: parsed.value ?? null };
  }
  return { kind: "literal", value: null };
}

function visualConditionsFromProjection(projection: unknown): { conditions: VisualCondition[]; join: "all" | "any" } | null {
  if (!projection || typeof projection !== "object" || !("kind" in projection)) return null;
  const node = projection as { children?: unknown[]; kind: string; operator?: VisualOperator; reference?: string; value?: unknown };
  if (node.kind === "condition" && node.operator && node.reference) return { conditions: [{ id: crypto.randomUUID(), operator: node.operator, reference: node.reference, value: normalizeProjectedValue(node.value) }], join: "all" };
  if ((node.kind !== "all" && node.kind !== "any") || !node.children) return null;
  const conditions = node.children.map((child) => visualConditionsFromProjection(child)).map((result) => result?.conditions[0]);
  if (conditions.some((condition) => !condition)) return null;
  return { conditions: conditions as VisualCondition[], join: node.kind };
}

function operatorsForField(field: WorkObjectFieldDefinition | undefined): VisualOperator[] {
  return isNumericField(field) ? ["filled", "empty", "eq", "neq", "gt", "gte", "lt", "lte"] : ["filled", "empty", "eq", "neq"];
}

function isNumericField(field: WorkObjectFieldDefinition | undefined) {
  return field ? ["currency", "duration", "formula", "number", "percent", "rating", "rollup"].includes(field.type) : false;
}
